/**
 * Document-head management and structured data.
 *
 * In a server-rendered production build this belongs in the framework's metadata
 * layer so crawlers receive it in the initial HTML. Here it is applied on the
 * client, which is sufficient to demonstrate the metadata contract.
 */

const upsertMeta = (selector: string, attrs: Record<string, string>) => {
  let el = document.head.querySelector<HTMLMetaElement>(selector);
  if (!el) {
    el = document.createElement('meta');
    document.head.appendChild(el);
  }
  Object.entries(attrs).forEach(([k, v]) => el!.setAttribute(k, v));
};

const upsertLink = (rel: string, href: string) => {
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
};

export const SITE_ORIGIN = 'https://velnorisexecutivenetwork.com';

export interface PageMeta {
  title: string;
  description: string;
  canonicalPath: string;
  noindex?: boolean;
  socialImageUrl?: string;
  type?: 'website' | 'article';
}

export const applyPageMeta = (meta: PageMeta): void => {
  document.title = meta.title;
  upsertMeta('meta[name="description"]', { name: 'description', content: meta.description });
  upsertMeta('meta[name="robots"]', {
    name: 'robots',
    content: meta.noindex ? 'noindex, nofollow' : 'index, follow',
  });
  upsertLink('canonical', `${SITE_ORIGIN}${meta.canonicalPath}`);
  upsertMeta('meta[property="og:title"]', { property: 'og:title', content: meta.title });
  upsertMeta('meta[property="og:description"]', { property: 'og:description', content: meta.description });
  upsertMeta('meta[property="og:type"]', { property: 'og:type', content: meta.type ?? 'website' });
  upsertMeta('meta[property="og:url"]', { property: 'og:url', content: `${SITE_ORIGIN}${meta.canonicalPath}` });
  upsertMeta('meta[name="twitter:card"]', { name: 'twitter:card', content: 'summary_large_image' });
  if (meta.socialImageUrl) {
    upsertMeta('meta[property="og:image"]', { property: 'og:image', content: meta.socialImageUrl });
  }
};

const STRUCTURED_DATA_ID = 'velnoris-structured-data';

export const applyStructuredData = (payload: unknown | null): void => {
  const existing = document.getElementById(STRUCTURED_DATA_ID);
  if (existing) existing.remove();
  if (!payload) return;
  const script = document.createElement('script');
  script.id = STRUCTURED_DATA_ID;
  script.type = 'application/ld+json';
  script.textContent = JSON.stringify(payload);
  document.head.appendChild(script);
};

export const organizationSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'Velnoris Executive Network',
  url: SITE_ORIGIN,
  description:
    'Retained executive search, board and C-suite recruitment, and confidential leadership mandates.',
  // Address, telephone and sameAs are intentionally omitted until verified.
});

export const breadcrumbSchema = (trail: { name: string; path: string }[]) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: trail.map((t, i) => ({
    '@type': 'ListItem',
    position: i + 1,
    name: t.name,
    item: `${SITE_ORIGIN}${t.path}`,
  })),
});

export const articleSchema = (a: {
  title: string;
  standfirst: string;
  authorName: string;
  publishedAt: string;
  updatedAt: string;
  slug: string;
  heroImageUrl?: string;
}) => ({
  '@context': 'https://schema.org',
  '@type': 'Article',
  headline: a.title,
  description: a.standfirst,
  author: { '@type': 'Organization', name: a.authorName },
  publisher: { '@type': 'Organization', name: 'Velnoris Executive Network' },
  datePublished: a.publishedAt,
  dateModified: a.updatedAt,
  mainEntityOfPage: `${SITE_ORIGIN}/insights/${a.slug}`,
  ...(a.heroImageUrl ? { image: a.heroImageUrl } : {}),
});

/**
 * JobPosting schema is emitted ONLY for verified opportunities with a confirmed
 * vacancy. Emitting it for a proposed or market-matched entry would represent an
 * unconfirmed role to search engines as a live vacancy.
 */
export const jobPostingSchema = (o: {
  title: string;
  status: string;
  clientDisclosure: string;
  disclosedClientName?: string;
  clientDescriptor: string;
  roleOverview: string;
  postedAt: string;
  applicationDeadline?: string;
  location: string;
  employmentType: string;
  referenceNumber: string;
  slug: string;
  compensation: { baseMin?: number; baseMax?: number; isEstimate: boolean };
}): unknown | null => {
  if (o.status !== 'verified' && o.status !== 'confidential_mandate') return null;
  const [city, region] = o.location.split(',').map((s) => s.trim());
  return {
    '@context': 'https://schema.org',
    '@type': 'JobPosting',
    title: o.title,
    identifier: { '@type': 'PropertyValue', name: 'Velnoris reference', value: o.referenceNumber },
    description: o.roleOverview,
    datePosted: o.postedAt,
    ...(o.applicationDeadline ? { validThrough: o.applicationDeadline } : {}),
    employmentType: o.employmentType.toUpperCase(),
    hiringOrganization: {
      '@type': 'Organization',
      name:
        o.clientDisclosure === 'disclosed' && o.disclosedClientName
          ? o.disclosedClientName
          : 'Confidential — represented by Velnoris Executive Network',
    },
    jobLocation: {
      '@type': 'Place',
      address: { '@type': 'PostalAddress', addressLocality: city, addressRegion: region, addressCountry: 'US' },
    },
    url: `${SITE_ORIGIN}/opportunities/${o.slug}`,
    // Salary is only asserted where the client has confirmed it.
    ...(!o.compensation.isEstimate && o.compensation.baseMin && o.compensation.baseMax
      ? {
          baseSalary: {
            '@type': 'MonetaryAmount',
            currency: 'USD',
            value: {
              '@type': 'QuantitativeValue',
              minValue: o.compensation.baseMin,
              maxValue: o.compensation.baseMax,
              unitText: 'YEAR',
            },
          },
        }
      : {}),
  };
};

/** Reference content for the administrator SEO screen. */
export const robotsTxt = `User-agent: *
Allow: /
Disallow: /portal/
Disallow: /sign-in
Disallow: /register
Disallow: /reset-password

Sitemap: ${SITE_ORIGIN}/sitemap.xml`;
